import { mark } from "@freightsimple/observability/observability";

const RELOAD_FLAG = "hubble:lazy-chunk-reload";
// Written over the flag's value once the reload has been counted, so the flag
// stays set (no second reload) but the next page load doesn't count it again.
const RECOVERY_NOTED = "noted";
const REASON_CHARS = 200;

// Set once this page has started a reload. Any error after that is told to
// stop, so nothing draws an error while the page unloads.
let reloading = false;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Unable to preload CSS")
  );
}

// Records the reload in the flag before it starts. Returns false when this tab
// already reloaded, or when sessionStorage is blocked: without the flag a
// failure that lasts would reload forever.
function claimReload(reason: string): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, reason.slice(0, REASON_CHARS));
    return true;
  } catch {
    return false;
  }
}

/**
 * When a dynamic import fails because the hashed chunk no longer exists on
 * the server (a deploy removed it), hard-reload once to fetch the new
 * index.html, which points at the new hashes. The sessionStorage flag allows
 * one reload per session and is never cleared: a file that loads on its own
 * when a screen renders would otherwise reload the page forever if it kept
 * failing, because each reload would reset the flag. When the browser knows
 * it is offline (navigator.onLine is false), a reload would only land on its
 * error page, so the caller handles it instead; a network that is up but not
 * reaching us still reloads.
 *
 * Returns true when a reload is under way and the caller should stop.
 */
export function reloadOnceForChunkError(error: unknown): boolean {
  if (reloading) return true;
  if (!isChunkLoadError(error) || !navigator.onLine) return false;
  if (!claimReload((error as Error).message)) return false;
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Called once Hubble has rendered. If the page got here through a stale-chunk
 * reload, counts it (with the error that caused it) as a `boot.stale_reload_recovered`
 * mark, since a reload that worked is otherwise reported nowhere.
 */
export function noteRecoveredReload(): void {
  // This page started a reload of its own: the flag is its reason, not a
  // recovery, and the next page load counts it.
  if (reloading) return;
  try {
    const reason = sessionStorage.getItem(RELOAD_FLAG);
    if (!reason || reason === RECOVERY_NOTED) return;
    sessionStorage.setItem(RELOAD_FLAG, RECOVERY_NOTED);
    mark("boot.stale_reload_recovered", { reason });
  } catch {
    // Storage blocked: claimReload never reloaded, so there is nothing to count.
  }
}

/**
 * Vite fires `vite:preloadError` on window whenever a dynamic import fails in
 * a production build, including the ones in ui-core helpers (Excel export,
 * spreadsheet preview, code blocks) that have no recovery of their own. The
 * error still reaches the caller, so their own handling is unchanged; this
 * only adds the reload-once for a stale chunk.
 *
 * Returns a function that removes the listener.
 */
export function listenForStaleChunks(): () => void {
  const onPreloadError = (event: VitePreloadErrorEvent): void => {
    reloadOnceForChunkError(event.payload);
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
}
